export type Params<Data, ThrowOnNotFound extends boolean> = {
  get: () => Promise<Data | null>
  set: (data: Data) => Promise<void>
  fallbackGet: () => Promise<Data | null>
  ignoreCache?: boolean
  throwOnNotFound?: ThrowOnNotFound
}

export async function getAndPopulateCache<
  Data,
  ThrowOnNotFound extends boolean,
>({
  get,
  set,
  fallbackGet,
  ignoreCache,
  revalidate,
  throwOnNotFound,
}: {
  get: () => Promise<Data | null>
  set: (data: Data | null) => Promise<void>
  fallbackGet: () => Promise<Data | null>
  ignoreCache?: boolean
  revalidate?: boolean
  throwOnNotFound: ThrowOnNotFound
}): Promise<ThrowOnNotFound extends true ? Data : Data | null> {
  if (ignoreCache) {
    const fresh = await fallbackGet()
    if (fresh === null && throwOnNotFound) throw new Error('Not found')
    if (revalidate) {
      await set(fresh)
    }
    return fresh as Data
  }

  if (revalidate) {
    const fresh = await fallbackGet()
    if (fresh === null && throwOnNotFound) throw new Error('Not found')
    await set(fresh)
    return fresh as Data
  }

  let data = await get()
  if (!data) {
    data = await fallbackGet()
    if (data === null) {
      if (throwOnNotFound) throw new Error('Not found')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return null as any
    }
    // populate cache
    await set(data)
  }

  return data as Data
}
